import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CancelIcon from '@mui/icons-material/Cancel';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { toast } from 'sonner';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { FEEDBACK_RATIONALE_MAX } from '../../types/analytics';
import type { FeedbackType, InsightFeedback } from '../../types/analytics';

interface InsightFeedbackButtonsProps {
  insightKey: string | null | undefined;
  weekStart: string;
  /** When false (older weeks), buttons render disabled with a tooltip. */
  writable: boolean;
}

const FEEDBACK_LABELS: Record<FeedbackType, string> = {
  accepted: 'Accept',
  rejected: 'Reject',
  addressed: 'Addressed',
};

const InsightFeedbackButtons: React.FC<InsightFeedbackButtonsProps> = ({
  insightKey,
  weekStart,
  writable,
}) => {
  const { insightFeedback, upsertInsightFeedback } = useAnalyticsStore();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRationale, setRejectRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Generator-produced insights without an insight_key (e.g. legacy rows)
  // can't be tied to feedback. Render nothing rather than confusing buttons.
  if (!insightKey) return null;

  const existing: InsightFeedback | undefined = insightFeedback.find(
    (f) => f.insight_key === insightKey
  );
  const currentType = existing?.feedback_type;

  // Performs the upsert and toasts on success/error. Rethrows on failure so
  // the Reject handler can keep its dialog open (and the user's rationale
  // text) instead of silently dropping it.
  const submit = async (feedback_type: FeedbackType, rationale?: string) => {
    setSubmitting(true);
    try {
      await upsertInsightFeedback({
        week_start: weekStart,
        insight_key: insightKey,
        feedback_type,
        rationale: rationale ? rationale.trim() : null,
      });
      toast.success(`Marked as ${FEEDBACK_LABELS[feedback_type].toLowerCase()}`);
    } catch (err) {
      console.error('Failed to save feedback:', err);
      toast.error('Failed to save feedback');
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = () => {
    submit('accepted').catch(() => {
      /* toast already shown */
    });
  };
  const handleAddressed = () => {
    submit('addressed').catch(() => {
      /* toast already shown */
    });
  };
  const handleReject = () => {
    setRejectRationale(existing?.rationale ?? '');
    setRejectOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (rejectRationale.length > FEEDBACK_RATIONALE_MAX) return;
    try {
      await submit('rejected', rejectRationale);
      setRejectOpen(false);
      setRejectRationale('');
    } catch {
      // Leave dialog open so the user can retry without losing their text.
    }
  };

  const buttonSx = { p: 0.25, ml: 0.25 };
  const iconSx = { fontSize: 18 };

  const tooltip = (label: string) =>
    writable ? label : `${label} (only the latest week's insights can receive feedback)`;

  return (
    <>
      <Box
        sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}
        data-testid={`feedback-controls-${insightKey}`}
      >
        <Tooltip title={tooltip(FEEDBACK_LABELS.accepted)}>
          <span>
            <IconButton
              size="small"
              sx={buttonSx}
              onClick={handleAccept}
              disabled={!writable || submitting}
              color={currentType === 'accepted' ? 'success' : 'default'}
              data-testid={`feedback-accept-${insightKey}`}
            >
              {currentType === 'accepted' ? (
                <CheckCircleIcon sx={iconSx} />
              ) : (
                <CheckCircleOutlineIcon sx={iconSx} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={tooltip(FEEDBACK_LABELS.rejected)}>
          <span>
            <IconButton
              size="small"
              sx={buttonSx}
              onClick={handleReject}
              disabled={!writable || submitting}
              color={currentType === 'rejected' ? 'error' : 'default'}
              data-testid={`feedback-reject-${insightKey}`}
            >
              {currentType === 'rejected' ? (
                <CancelIcon sx={iconSx} />
              ) : (
                <HighlightOffIcon sx={iconSx} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={tooltip(FEEDBACK_LABELS.addressed)}>
          <span>
            <IconButton
              size="small"
              sx={buttonSx}
              onClick={handleAddressed}
              disabled={!writable || submitting}
              color={currentType === 'addressed' ? 'primary' : 'default'}
              data-testid={`feedback-addressed-${insightKey}`}
            >
              {currentType === 'addressed' ? (
                <DoneAllIcon sx={iconSx} />
              ) : (
                <TaskAltIcon sx={iconSx} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Reject insight</DialogTitle>
        <DialogContent dividers>
          <TextField
            value={rejectRationale}
            onChange={(e) => setRejectRationale(e.target.value)}
            placeholder="Why is this insight wrong or unhelpful?"
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            autoFocus
            error={rejectRationale.length > FEEDBACK_RATIONALE_MAX}
            helperText={`${rejectRationale.length}/${FEEDBACK_RATIONALE_MAX}${
              rejectRationale.length > FEEDBACK_RATIONALE_MAX ? ' — too long' : ''
            }`}
            inputProps={{ 'data-testid': 'feedback-reject-rationale-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRejectSubmit}
            variant="contained"
            color="error"
            disabled={submitting || rejectRationale.length > FEEDBACK_RATIONALE_MAX}
            data-testid="feedback-reject-submit"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InsightFeedbackButtons;
